import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { LocalStorageService } from './local-storage.service';
import {
    CacheStorageType,
    HttpCacheConfig,
    CachedResponse,
    CacheEntry,
    RequestParams,
    DEFAULT_CACHE_CONFIG,
    CACHE_KEY_PREFIX,
} from './http-cache-config.interface';
import { generateCacheKey } from '../helper/cache-key-generator.helper';
import { simpleHash } from '../helper/cache-key-generator.helper';

/**
 * Service for caching HTTP responses with configurable storage and expiration
 *
 * Usage:
 * ```typescript
 * const config: HttpCacheConfig = {
 *   storageType: CacheStorageType.LOCAL,
 *   timeoutMinutes: 10
 * };
 *
 * return this.httpCache.getCachedOrFetch(
 *   requestParams,
 *   () => this.http.get<T>(url),
 *   config
 * );
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class HttpCacheService {
    /**
     * In-memory cache storage
     * Used when storageType is MEMORY
     */
    private memoryCache: Map<string, CacheEntry<any>> = new Map();

    /**
     * Current auth token hash for cache key generation
     * Using hash to avoid storing actual token
     */
    private currentAuthTokenHash: string | null = null;

    constructor(private localStorageService: LocalStorageService) {}

    /**
     * Set the current auth token for cache key generation
     * This will clean up all cache entries from previous auth tokens
     *
     * @param authToken The current user's auth token
     */
    public setAuthToken(authToken: string | null): void {
        const newTokenHash = authToken ? simpleHash(authToken) : null;

        // If token changed, clean up old cache entries
        if (this.currentAuthTokenHash !== newTokenHash) {
            if (newTokenHash) {
                this.cleanupOldAuthTokenEntries(newTokenHash);
            }
            this.currentAuthTokenHash = newTokenHash;
        }
    }

    /**
     * Get current auth token hash (useful for debugging)
     */
    public getCurrentAuthTokenHash(): string | null {
        return this.currentAuthTokenHash;
    }

    /**
     * Main method: Get cached data or fetch fresh data if cache miss/expired
     *
     * @param requestParams Parameters that uniquely identify this request
     * @param fetchFn Function that returns Observable to fetch fresh data
     * @param config Cache configuration
     * @returns Observable of CachedResponse with data and timestamp
     */
    public getCachedOrFetch<T>(
        requestParams: RequestParams,
        fetchFn: () => Observable<T>,
        config: HttpCacheConfig
    ): Observable<CachedResponse<T>> {
        // Merge with defaults
        const finalConfig: HttpCacheConfig = {
            ...DEFAULT_CACHE_CONFIG,
            ...config,
        } as HttpCacheConfig;

        // If caching disabled or force refresh, fetch immediately
        if (finalConfig.storageType === CacheStorageType.NONE || finalConfig.forceRefresh) {
            return this.fetchAndCache(requestParams, fetchFn, finalConfig);
        }

        // Generate cache key (auth token from config or from stored value)
        const cacheKey = this.buildCacheKey(
            requestParams,
            finalConfig.keyPrefix!,
            finalConfig.authToken
        );

        // Try to get from cache
        const cachedEntry = this.getFromStorage<T>(cacheKey, finalConfig.storageType);

        // Check if cache is valid
        if (cachedEntry && this.isCacheValid(cachedEntry)) {
            return of({
                data: cachedEntry.data,
                cachedAt: cachedEntry.cachedAt,
                expiresAt: cachedEntry.expiresAt,
                cacheKey,
            });
        }

        // Cache miss or expired - fetch fresh data
        return this.fetchAndCache(requestParams, fetchFn, finalConfig);
    }

    /**
     * Invalidate all cache entries for a specific storage type
     *
     * @param storageType Type of storage to clear (defaults to all types)
     */
    public invalidateAll(storageType?: CacheStorageType): void {
        if (!storageType || storageType === CacheStorageType.MEMORY) {
            this.memoryCache.clear();
        }

        if (!storageType || storageType === CacheStorageType.SESSION) {
            this.clearStorageByPrefix('sessionStorage');
        }

        if (!storageType || storageType === CacheStorageType.LOCAL) {
            this.clearStorageByPrefix('localStorage');
        }
    }

    /**
     * Invalidate cache entries matching a specific prefix
     *
     * @param prefix Key prefix to match
     * @param storageType Storage type to clear from
     */
    public invalidateByPrefix(prefix: string, storageType: CacheStorageType): void {
        // Key format: cache-{tokenHash}-{prefix}-{requestHash}
        const prefixSegment = `-${prefix}-`;
        if (storageType === CacheStorageType.MEMORY) {
            const keysToDelete: string[] = [];
            this.memoryCache.forEach((_, key) => {
                if (key.includes(prefixSegment)) {
                    keysToDelete.push(key);
                }
            });
            keysToDelete.forEach(key => this.memoryCache.delete(key));
        } else if (storageType === CacheStorageType.SESSION) {
            this.clearStorageByPrefix('sessionStorage', prefixSegment);
        } else if (storageType === CacheStorageType.LOCAL) {
            this.clearStorageByPrefix('localStorage', prefixSegment);
        }
    }

    /**
     * Invalidate a specific cache entry
     *
     * @param requestParams Request parameters to generate cache key
     * @param storageType Storage type
     * @param keyPrefix Key prefix used in original cache config
     */
    public invalidateSpecific(
        requestParams: RequestParams,
        storageType: CacheStorageType,
        keyPrefix: string = 'qwyk_cache'
    ): void {
        const cacheKey = this.buildCacheKey(requestParams, keyPrefix);

        if (storageType === CacheStorageType.MEMORY) {
            this.memoryCache.delete(cacheKey);
        } else if (storageType === CacheStorageType.SESSION) {
            sessionStorage.removeItem(cacheKey);
        } else if (storageType === CacheStorageType.LOCAL) {
            this.localStorageService.remove(cacheKey);
        }
    }

    /**
     * Private: Fetch fresh data and cache it
     */
    private fetchAndCache<T>(
        requestParams: RequestParams,
        fetchFn: () => Observable<T>,
        config: HttpCacheConfig
    ): Observable<CachedResponse<T>> {
        const cacheKey = this.buildCacheKey(
            requestParams,
            config.keyPrefix!,
            config.authToken
        );

        return fetchFn().pipe(
            tap(data => {
                if (config.storageType !== CacheStorageType.NONE) {
                    const entry = this.createCacheEntry(data, config.timeoutMinutes);
                    this.saveToStorage(cacheKey, entry, config.storageType);
                }
            }),
            map(data => {
                const now = new Date();
                const expiresAt = new Date(now.getTime() + config.timeoutMinutes * 60000);
                return {
                    data,
                    cachedAt: now.toISOString(),
                    expiresAt: expiresAt.toISOString(),
                    cacheKey,
                };
            })
        );
    }

    /**
     * Private: Build cache key from request parameters
     * Format: cache-{authTokenHash}-{prefix}-{requestHash}
     * This ensures cache entries are scoped to the authenticated user
     */
    private buildCacheKey(params: RequestParams, prefix: string, authToken?: string): string {
        // Use provided auth token or fall back to stored one
        const tokenHash = authToken
            ? simpleHash(authToken)
            : this.currentAuthTokenHash;

        // Build key with or without auth token
        const requestHash = generateCacheKey(params);
        return `${CACHE_KEY_PREFIX}-${tokenHash || 'noauth'}-${prefix}-${requestHash}`;
    }

    /**
     * Private: Create cache entry with expiration
     */
    private createCacheEntry<T>(data: T, timeoutMinutes: number): CacheEntry<T> {
        const now = new Date();
        const expiresAt = new Date(now.getTime() + timeoutMinutes * 60000);

        return {
            data,
            cachedAt: now.toISOString(),
            expiresAt: expiresAt.toISOString(),
        };
    }

    /**
     * Private: Check if cache entry is still valid
     */
    private isCacheValid<T>(entry: CacheEntry<T>): boolean {
        const now = new Date();
        const expiresAt = new Date(entry.expiresAt);
        return now < expiresAt;
    }

    /**
     * Private: Get from storage based on type
     */
    private getFromStorage<T>(key: string, storageType: CacheStorageType): CacheEntry<T> | null {
        try {
            switch (storageType) {
                case CacheStorageType.MEMORY:
                    return this.memoryCache.get(key) || null;

                case CacheStorageType.SESSION:
                    const sessionData = sessionStorage.getItem(key);
                    return sessionData ? JSON.parse(sessionData) : null;

                case CacheStorageType.LOCAL:
                    const localData = this.localStorageService.getObject(key);
                    return localData ? (localData as CacheEntry<T>) : null;

                default:
                    return null;
            }
        } catch (error) {
            console.error('Error reading from cache:', error);
            return null;
        }
    }

    /**
     * Private: Save to storage based on type
     */
    private saveToStorage<T>(key: string, entry: CacheEntry<T>, storageType: CacheStorageType): void {
        try {
            switch (storageType) {
                case CacheStorageType.MEMORY:
                    this.memoryCache.set(key, entry);
                    break;

                case CacheStorageType.SESSION:
                    sessionStorage.setItem(key, JSON.stringify(entry));
                    break;

                case CacheStorageType.LOCAL:
                    this.localStorageService.setObject(key, entry);
                    break;
            }
        } catch (error) {
            console.error('Error saving to cache:', error);
        }
    }

    /**
     * Private: Clear storage by prefix
     */
    private clearStorageByPrefix(
        storageType: 'sessionStorage' | 'localStorage',
        prefix?: string
    ): void {
        try {
            const storage = storageType === 'sessionStorage' ? sessionStorage : window.localStorage;
            const keysToRemove: string[] = [];

            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                if (key && (!prefix || key.includes(prefix))) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => storage.removeItem(key));
        } catch (error) {
            console.error('Error clearing storage:', error);
        }
    }

    /**
     * Private: Clean up cache entries from old auth tokens
     * Removes all cache entries that don't match the current auth token
     *
     * @param currentTokenHash Hash of the current auth token to keep
     */
    private cleanupOldAuthTokenEntries(currentTokenHash: string): void {
        const cachePrefix = CACHE_KEY_PREFIX;
        const currentPrefix = `${cachePrefix}-${currentTokenHash}-`;

        // Clean memory cache
        const memoryKeysToDelete: string[] = [];
        this.memoryCache.forEach((_, key) => {
            // Keep entries that start with our prefix OR match current token
            if (key.startsWith(cachePrefix) && !key.startsWith(currentPrefix)) {
                memoryKeysToDelete.push(key);
            }
        });
        memoryKeysToDelete.forEach(key => this.memoryCache.delete(key));

        // Clean session storage
        this.cleanupStorageByAuthToken('sessionStorage', cachePrefix, currentPrefix);

        // Clean local storage
        this.cleanupStorageByAuthToken('localStorage', cachePrefix, currentPrefix);
    }

    /**
     * Private: Clean up storage entries from old auth tokens
     */
    private cleanupStorageByAuthToken(
        storageType: 'sessionStorage' | 'localStorage',
        cachePrefix: string,
        currentPrefix: string
    ): void {
        try {
            const storage = storageType === 'sessionStorage' ? sessionStorage : window.localStorage;
            const keysToRemove: string[] = [];

            for (let i = 0; i < storage.length; i++) {
                const key = storage.key(i);
                // Remove cache entries that don't match current auth token
                if (key && key.startsWith(cachePrefix) && !key.startsWith(currentPrefix)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach(key => storage.removeItem(key));
        } catch (error) {
            console.error('Error cleaning up old auth token entries:', error);
        }
    }
}
